import { useEffect, useState } from 'react'
import { CarteAvis, libelleCritere } from './CarteAvis'
import { Etoiles } from './Etoiles'
import { ErreurApi } from '../api'
import {
  lireReputationBien,
  lireReputationFoyer,
  lireReputationUtilisateur,
  type CritereAvis,
  type Reputation,
} from '../apiReputation'
import { TEXTES, messageErreur, type Langue } from '../i18n'

/**
 * Le bloc de réputation — profil, fiche d'annonce, foyer.
 *
 * **Sous trois avis, il affiche « Nouveau » et aucun chiffre** (spec §8.2-6). Le serveur rend
 * d'ailleurs les trois notes nulles dans ce cas : rien à masquer ici, et c'est voulu — un front qui
 * recevrait la note en la « masquant » finirait par la laisser fuir.
 *
 * **Les trois moyennes sont affichées, pas une** (D32, conception L8 §1.7). Quelqu'un qui compte
 * lui-même et trouve 4,6 là où l'écran affiche 4,3 doit comprendre l'écart, au lieu de conclure que
 * la plateforme triche. La ligne d'explication réutilise le motif de L5.
 */

export type Cible =
  | { nature: 'utilisateur'; id: string }
  | { nature: 'bien'; id: string }
  | { nature: 'foyer'; id: string }

/** Ce que la lecture a produit : les trois états d'un chargement, jamais confondus. */
export interface EtatReputation {
  reputation: Reputation | null
  erreur: string | null
  chargement: boolean
}

interface BlocReputationProps {
  cible: Cible
  langue: Langue
  /** Le référentiel, quand l'écran l'a déjà chargé. Sinon les codes bruts s'affichent. */
  criteres?: CritereAvis[]
  titre?: string
}

/**
 * La lecture, séparée de son affichage — ticket `fo-annonce-05`.
 *
 * **La fiche d'annonce rend la même réputation à deux endroits** : la note en résumé dans la
 * colonne de droite, sous le propriétaire, et les avis dans le corps. Deux composants qui liraient
 * chacun de leur côté doubleraient une réponse qui porte les avis eux-mêmes — c'est-à-dire la
 * partie lourde. Le crochet lit une fois, les deux vues se servent.
 *
 * `cible` accepte `null` : sur la fiche, l'identifiant du bien n'existe qu'une fois l'annonce
 * chargée, et un crochet ne se déclare pas sous condition.
 */
export function useReputation(cible: Cible | null, langue: Langue): EtatReputation {
  const [reputation, setReputation] = useState<Reputation | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)

  const nature = cible?.nature ?? null
  const id = cible?.id ?? null

  useEffect(() => {
    if (nature === null || id === null) {
      return
    }

    setChargement(true)

    const lecture =
      nature === 'utilisateur'
        ? lireReputationUtilisateur(id)
        : nature === 'bien'
          ? lireReputationBien(id)
          : lireReputationFoyer(id)

    void lecture
      .then(setReputation)
      .catch((cause: unknown) =>
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
      )
      .finally(() => setChargement(false))
  }, [id, nature, langue])

  return { reputation, erreur, chargement }
}

export function BlocReputation({ cible, langue, criteres = [], titre }: BlocReputationProps) {
  const etat = useReputation(cible, langue)

  return <VueReputation etat={etat} langue={langue} criteres={criteres} titre={titre} />
}

/**
 * La note seule, en résumé — ticket `fo-annonce-05`.
 *
 * **La même anatomie que le bloc du propriétaire, et c'est le point.** Les deux réputations que la
 * fiche porte sont distinctes (conception L8, §1.9) mais elles se lisent ensemble : « à qui ai-je
 * affaire » et « comment est le logement » sont la même question posée deux fois. Les mettre l'une
 * sous l'autre, dans la même forme, c'est ce qui permet de les comparer ; les séparer d'un écran de
 * défilement, c'est n'en lire qu'une.
 *
 * Les avis eux-mêmes restent dans le corps, où ils ont la largeur de se lire.
 */
export function NoteResume({
  etat,
  langue,
  titre,
}: {
  etat: EtatReputation
  langue: Langue
  titre: string
}) {
  const t = TEXTES[langue]
  const { reputation, erreur, chargement } = etat

  // Une erreur de réputation n'a pas à occuper la colonne : la fiche vaut sans sa note, et un
  // message d'échec sous le propriétaire ferait douter du reste.
  if (chargement || erreur !== null || reputation === null) {
    return null
  }

  const nombre = new Intl.NumberFormat(langue)

  return (
    <div className="bloc fiche__bailleur">
      <p className="fiche__bailleur-titre">{titre}</p>
      {reputation.isDisplayable ? (
        <p className="fiche__note-bailleur">
          <Etoiles note={reputation.displayedRating ?? 0} langue={langue} libelle={titre} />
          <span className="note__valeur">
            {new Intl.NumberFormat(langue, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              .format(reputation.displayedRating ?? 0)}
          </span>
          <span className="aide">
            {nombre.format(reputation.reviewCount)} {t.avisNombre}
          </span>
        </p>
      ) : (
        <p className="aide">{t.avisNouveau}</p>
      )}
    </div>
  )
}

/** L'affichage complet : la note, son explication, les critères et les avis. */
export function VueReputation({
  etat,
  langue,
  criteres = [],
  titre,
}: {
  etat: EtatReputation
  langue: Langue
  criteres?: CritereAvis[]
  titre?: string
}) {
  const t = TEXTES[langue]
  const { reputation, erreur, chargement } = etat

  if (chargement) {
    return <p className="aide">{t.chargement}</p>
  }

  if (erreur) {
    return <p className="aide">{erreur}</p>
  }

  if (!reputation) {
    return null
  }

  const nombre = new Intl.NumberFormat(langue)

  const note = new Intl.NumberFormat(langue, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  return (
    <section className="bloc-reputation">
      <h3>{titre ?? t.avisReputation}</h3>

      {!reputation.isDisplayable ? (
        <p className="aide">
          <strong>{t.avisNouveau}</strong> — {t.avisNouveauAide}
        </p>
      ) : (
        <>
          <p className="bloc-reputation__note">
            <Etoiles
              note={reputation.displayedRating ?? 0}
              langue={langue}
              libelle={t.avisReputation}
            />
            <strong>{note.format(reputation.displayedRating ?? 0)}</strong>
            <span className="aide">
              {nombre.format(reputation.reviewCount)} {t.avisNombre}
            </span>
          </p>

          {/* La ligne d'explication de L5, reutilisee : le chiffre affiche doit pouvoir se defendre. */}
          <p className="aide ligne-explication">
            {t.avisPondereeAide} ({nombre.format(reputation.halfLifeMonths)}) —{' '}
            {t.avisMoyenneBrute} {note.format(reputation.plainAverageRating ?? 0)}
          </p>

          {reputation.perCriterion.length > 0 && (
            <>
              <h4>{t.avisParCritere}</h4>
              <ul className="repartition-criteres">
                {reputation.perCriterion.map((entree) => (
                  <li key={entree.criterionCode}>
                    <span>{libelleCritere(criteres, entree.criterionCode, langue)}</span>
                    <span
                      className="barre-repartition"
                      style={{ inlineSize: `${(entree.average / 5) * 100}%` }}
                      aria-hidden="true"
                    />
                    <span>{note.format(entree.average)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <ul className="liste-avis">
        {reputation.reviews.items.map((avis) => (
          <li key={avis.id}>
            <CarteAvis avis={avis} langue={langue} criteres={criteres} />
          </li>
        ))}
      </ul>
    </section>
  )
}
